<script setup lang="ts">
import { DateTime } from 'luxon'
import useUserStore from '~/store/user'
import usePoolStore from '~/store/pool'
import {
    type Checkin,
    type Comment,
    FeedbackType,
    type Report,
    type Feedback as FeedbackResponse
} from '~/interfaces/feedback'
import FeedbackListHeader from '~/components/FeedbackList/FeedbackListHeader.vue'
import type { ComputedRef } from 'vue'
import CommentModal from './CommentModal.vue'
import UserNotConnectedModal from '../Common/UserNotConnectedModal.vue'
import IconClock from '../Icons/IconClock.vue'

const props = defineProps({
    locale: {
        type: String,
        required: true
    },
    withModal: {
        type: Boolean,
        default: false
    },
    isHeightLimited: {
        type: Boolean,
        default: false
    },
    isPartner: {
        type: Boolean,
        default: false
    }
})

const emits = defineEmits(['openUserNotConnectedModal'])

const userStore = useUserStore()
const poolStore = usePoolStore()
const config = useRuntimeConfig()
const { t } = useI18n()
const toast = useToast()

const isUserNotConnectedModalOpen = ref(false)
const commentModal = useTemplateRef('commentModal')

const ratingDetailPopup = useTemplateRef('ratingDetailPopup')

const displayViewMoreFeedbacksButton = computed(
    () =>
        poolStore.feedbacksMinIndex + poolStore.feedbacksLimit <
        poolStore.totalFeedbacksCount
)

const feedbacksToShow: ComputedRef<FeedbackResponse[] | null> = computed(() =>
    poolStore.feedbacksFilter
        ? poolStore.filteredFeedbacks
        : poolStore.unfilteredFeedbacks
)

const hasFeedbacksToShow = computed(
    () => !!feedbacksToShow.value?.length || false
)

watch(
    () => poolStore.feedbacksMinIndex,
    () => {
        if (poolStore.feedbacksMinIndex) {
            poolStore.fetchFeedbacksFromCommunityFeedbacks()
        }
    }
)

watch(
    () => poolStore.poolId,
    () => {
        if (poolStore.poolId) {
            poolStore.fetchFeedbacksFromCommunityFeedbacks()
        }
    }
)

const setFeedbackHeight = () => {
    setTimeout(() => {
        const feedbackEl = document.getElementById('feedback-list')

        if (feedbackEl) {
            feedbackEl.style.maxHeight = `${feedbackEl.clientHeight}px`
        }
    }, 1000)
}

const getCommonProps = (feedback: FeedbackResponse) => ({
    type: feedback.type.toLowerCase(),
    feedbackId: feedback.id,
    user: {
        id: feedback.userId || '',
        username: feedback.userUsername || '',
        passState: feedback.userPassState,
        hasChargedWithPass: (feedback as Checkin)?.isChargeWithPass
    },
    isAnonymous: feedback.isAnonymous,
    creationDate: feedback.creationDate
        ? DateTime.fromISO(feedback.creationDate)
        : undefined,
    rating: {
        globalScore: feedback.rating?.globalNote,
        locationScore: feedback.rating?.locationNote,
        materialScore: feedback.rating?.materialNote,
        priceScore: feedback.rating?.priceNote,
        securityScore: feedback.rating?.securityNote
    },
    mustDisplayWholeComment: false,
    response: {
        content: feedback.chargemapResponse?.publicContent,
        name: 'Chargemap',
        creationDate:
            feedback.chargemapResponse &&
            feedback.chargemapResponse.creationDate
                ? DateTime.fromISO(feedback.chargemapResponse.creationDate)
                : undefined
    },
    responseSecondary: {
        content: feedback.partnerResponse?.content || '',
        name: feedback.partnerResponse?.partnerName || '',
        creationDate:
            feedback.partnerResponse && feedback.partnerResponse.creationDate
                ? DateTime.fromISO(feedback.partnerResponse.creationDate)
                : undefined
    },
    hasBeenRejected: false,
    hasAlreadyEditedComment: (feedback as Comment).isCommentModerated ?? false
})

const getCheckinProps = (feedback: FeedbackResponse) => {
    if (feedback.type === FeedbackType.CHECKIN) {
        return {
            chargeStartDate: (feedback as Checkin).chargeStartDate
                ? DateTime.fromISO((feedback as Checkin).chargeStartDate)
                : undefined,
            chargeEndDate: (feedback as Checkin).chargeEndDate
                ? DateTime.fromISO(
                      (feedback as Checkin).chargeEndDate as string
                  )
                : undefined,
            connector: {
                type: (feedback as Checkin).connectorType,
                maxPower: (feedback as Checkin).connectorMaxPower
            },
            vehicle: {
                brandName: (feedback as Checkin).vehicleBrandName,
                modelName: (feedback as Checkin).vehicleModelName,
                versionName: (feedback as Checkin).vehicleVersionName
            },
            comment: (feedback as Checkin).rating?.comment
        }
    }

    return {}
}

const getCommentProps = (feedback: FeedbackResponse) => {
    if (feedback.type === FeedbackType.COMMENT) {
        return {
            content: (feedback as Comment).comment
        }
    }

    return {}
}

const getReportProps = (feedback: FeedbackResponse) => {
    if (feedback.type === FeedbackType.REPORT) {
        return {
            reasonType: (feedback as Report).reasonType,
            isOperatorWarned: (feedback as Report).isOperatorWarned ?? false,
            comment:
                (feedback as Report).comment ??
                (feedback as Report).rating?.comment
        }
    }

    return {}
}

const getFeedbackType = (feedback: FeedbackResponse) =>
    feedback.type.toLowerCase()

const onClickViewMoreButton = () => {
    poolStore.setFeedbacksMinIndex(
        poolStore.feedbacksMinIndex + poolStore.feedbacksLimit
    )
}

const getDisplayShowOlderCommentsLabelProp = (feedback: FeedbackResponse) =>
    feedback.displayShowOlderCommentsLabel

const onClickGlobalScoreAverage = () => {
    if (ratingDetailPopup.value) {
        ratingDetailPopup.value.show()
    }
}

const onClickComment = () => {
    if (userStore.userId && commentModal.value) {
        commentModal.value.show()
    } else if (props.withModal) {
        isUserNotConnectedModalOpen.value = true
    } else {
        emits('openUserNotConnectedModal')
    }
}

const onSendComment = async (
    commentContent: string,
    isNonAnonymous: boolean
) => {
    try {
        await poolStore.postCommentToCommunityFeedbacks(
            commentContent,
            isNonAnonymous
        )
        toast.success(t('comment.successfully_sent'))
    } catch {
        toast.error(t('comment.error_while_sending'))
    }
}

onMounted(async () => {
    if (isIframe()) {
        await poolStore.fetchFeedbacksFromCommunityFeedbacks()
    }

    if (props.isHeightLimited) {
        setFeedbackHeight()
    }
})
</script>

<template>
    <div
        class="flex flex-col gap-8 p-6 items-center box-border bg-white flex-grow w-full relative overflow-y-auto"
    >
        <FeedbackListHeader
            :is-partner
            @onClickComment="onClickComment"
            @onClickGlobalScoreAverage="onClickGlobalScoreAverage"
        />

        <Loader v-if="poolStore.communityFeedbacksLoader" />
        <div
            v-else-if="hasFeedbacksToShow"
            id="feedback-list"
            class="flex flex-col items-center gap-4 w-full overflow-y-auto"
        >
            <Feedback
                v-for="(feedback, index) in feedbacksToShow"
                :key="index"
                :feedback-type="getFeedbackType(feedback)"
                :common-props="getCommonProps(feedback)"
                :comment-props="getCommentProps(feedback)"
                :checkin-props="getCheckinProps(feedback)"
                :report-props="getReportProps(feedback)"
                :display-older-comments-label="
                    getDisplayShowOlderCommentsLabelProp(feedback)
                "
                :locale
                :cdn-url="config.app.cdnURL"
            />
            <ChargemapB2CButton
                v-if="displayViewMoreFeedbacksButton"
                type="tertiary"
                class="mb-8"
                @click="onClickViewMoreButton"
            >
                <template #icon>
                    <IconClock class="h-6" />
                </template>
                {{ $t('see_more') }}
            </ChargemapB2CButton>
        </div>
        <div v-else class="flex flex-col gap-4 items-center py-4 mb-10">
            <div
                class="flex flex-col gap-1 items-center text-fifteen-exploitation leading-4.5 text-center"
            >
                <img
                    class="mb-3"
                    :src="`${config.app.cdnURL}/images/NoContribution.svg`"
                    :alt="$t('no_contribution_yet.title')"
                />
                <span class="font-semibold text-gray-800">
                    {{ $t('no_contribution_yet.title') }}
                </span>
                <span class="font-normal text-gray-500">
                    {{ $t('no_contribution_yet.subtitle') }}
                </span>
            </div>
            <ChargemapB2CButton
                type="primary"
                size="large"
                @click="onClickComment"
            >
                {{ $t('comment.leave') }}
            </ChargemapB2CButton>
        </div>
    </div>
    <RatingDetailPopup
        ref="ratingDetailPopup"
        :global-score-average="poolStore.getGlobalScoreAverage"
        :material-score-average="poolStore.getMaterialScoreAverage"
        :price-score-average="poolStore.getPriceScoreAverage"
        :location-score-average="poolStore.getLocationScoreAverage"
        :security-score-average="poolStore.getSecurityScoreAverage"
        :cdn-url="config.app.cdnURL"
    >
        <template #title>
            {{ $t('rating_popup.average.title') }}
        </template>
        <template #subtitle>
            {{ $t('rating_popup.average.subtitle') }}
        </template>
    </RatingDetailPopup>
    <CommentModal ref="commentModal" @send="onSendComment" />
    <div
        v-if="withModal && isUserNotConnectedModalOpen"
        class="absolute inset-0"
    >
        <UserNotConnectedModal
            @closePopUp="isUserNotConnectedModalOpen = false"
        />
    </div>
</template>
